import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Popover, Grid } from '@material-ui/core';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import format from 'date-fns/format';
import {
  subWeeks,
  subMonths,
  isBefore,
  isAfter,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from 'date-fns';
import '../CostAnalysis/costAnalysis.css';
import { Typography } from '@mui/material';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { ReactComponent as CalenderIcon } from '../../assets/ICONS/calender.svg';
import moment from 'moment';

interface DateTimeSelectorProps {
  startDate: Date;
  endDate: Date;
  onDateChange: (startDate: Date, endDate: Date) => void;
  /**
   * Number of months in the past that are selectable
   * @default 6
   */
  allowedPastMonths?: number;
  /**
   * Whether to show the preset range selection section
   * @default false
   */
  showPresetRanges?: boolean;
  /**
   * Custom preset ranges to display (only used if showPresetRanges is true)
   * If not provided, default presets will be used
   */
  customPresetRanges?: PresetRange[];
  /**
   * Custom button text format
   * @default 'dd-MMM-yyyy'
   */
  dateFormat?: string;

  startDateMinDate?: Date;
  startDateMaxDate?: Date;
}

interface PresetRange {
  label: string;
  value: string;
  getDateRange: () => { start: Date; end: Date };
}

const DateTimeSelector: React.FC<DateTimeSelectorProps> = ({
  startDate,
  endDate,
  onDateChange,
  allowedPastMonths = 6, // Default 6 months
  showPresetRanges = false,
  customPresetRanges,
  dateFormat = 'dd-MMM-yyyy',
  startDateMinDate,
  startDateMaxDate
}) => {
  const useStyles = makeStyles((theme) => ({
    datePicker: {
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#0078db',
      },
      '& .MuiInputLabel-outlined.Mui-focused': {
        color: '#0078db',
      },
    },
  }));
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tempStartDate, setTempStartDate] = useState<Date>(startDate);
  const [tempEndDate, setTempEndDate] = useState<Date>(endDate);
  const [dateError, setDateError] = useState<string>('');
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const classes = useStyles();

  const calculateEndDateMax = (startDate: Date): Date => {
    const maxAllowed = moment(startDate).add(15, 'days');
    const today = new Date();
    return maxAllowed.isAfter(today) ? today : maxAllowed.toDate();
  };

  // Default preset ranges (used if showPresetRanges is true and no custom ranges provided)
  const defaultPresetRanges: PresetRange[] = [
    {
      label: 'Last Week',
      value: 'lastweek',
      getDateRange: () => {
        const today = new Date();
        const lastWeekStart = startOfWeek(subWeeks(today, 1), {
          weekStartsOn: 1,
        });
        const lastWeekEnd = endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
        return {
          start: lastWeekStart,
          end: lastWeekEnd,
        };
      },
    },
    {
      label: 'Last Month',
      value: 'lastmonth',
      getDateRange: () => {
        const today = new Date();
        const lastMonth = subMonths(today, 1);
        return {
          start: startOfMonth(lastMonth),
          end: endOfMonth(lastMonth),
        };
      },
    },
    {
      label: 'Last 3 Months',
      value: 'last3months',
      getDateRange: () => {
        const today = new Date();
        const threeMonthsAgo = subMonths(today, 3);
        return {
          start: startOfMonth(threeMonthsAgo),
          end: endOfMonth(subMonths(today, 1)),
        };
      },
    },
    {
      label: 'Last 6 Months',
      value: 'last6months',
      getDateRange: () => {
        const today = new Date();
        const sixMonthsAgo = subMonths(today, 6);
        return {
          start: startOfMonth(sixMonthsAgo),
          end: endOfMonth(subMonths(today, 1)),
        };
      },
    },
  ];

  // Use custom preset ranges if provided, otherwise use defaults
  const presetRanges = customPresetRanges || defaultPresetRanges;

  // Get the minimum allowed date based on allowedPastMonths
  // This ensures full months are selectable regardless of current date
  const getMinDate = (): Date => {
    const today = new Date();
    const monthsAgo = subMonths(today, allowedPastMonths);
    // Return the start of the month from allowedPastMonths ago
    return startOfMonth(monthsAgo);
  };

  // Function to check if current date range matches any preset
  const getCurrentPreset = (): string => {
    if (!showPresetRanges) return '';

    for (const preset of presetRanges) {
      const { start, end } = preset.getDateRange();
      if (
        format(startDate, 'yyyy-MM-dd') === format(start, 'yyyy-MM-dd') &&
        format(endDate, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd')
      ) {
        return preset.value;
      }
    }
    return '';
  };

  // Update selected preset when component mounts or dates change
  useEffect(() => {
    if (showPresetRanges) {
      const currentPreset = getCurrentPreset();
      setSelectedPreset(currentPreset);
    }
  }, [startDate, endDate, showPresetRanges]);

  const handleDateRangeInputClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setDateError('');
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    // Validate the date range
    if (validateDateRange()) {
      onDateChange(tempStartDate, tempEndDate);
      handleClose();
    }
  };

  const validateDateRange = (): boolean => {
    const minDate = getMinDate();
    const today = new Date();

    if (isBefore(tempStartDate, minDate)) {
      const monthsText = allowedPastMonths === 1 ? 'month' : 'months';
      setDateError(
        `Start date cannot be more than ${allowedPastMonths} ${monthsText} in the past`
      );
      return false;
    }

    if (isAfter(tempEndDate, today)) {
      setDateError('End date cannot be in the future');
      return false;
    }

    if (isAfter(tempStartDate, tempEndDate)) {
      setDateError('Start date must be before end date');
      return false;
    }

    return true;
  };

  const handleStartDateChange = (date: MaterialUiPickersDate) => {
    if (date) {
      const newStart = date;
      const now = new Date();

      const fifteenDaysLater = moment(newStart).add(15, 'days').toDate();
      const newEnd = isAfter(fifteenDaysLater, now) ? now : fifteenDaysLater;

      setTempStartDate(newStart);
      setTempEndDate(newEnd);
      setDateError('');
    }
  };

  const handleEndDateChange = (date: MaterialUiPickersDate) => {
    if (date) {
      let newEnd = date;
      const now = new Date();

      if (isAfter(newEnd, now)) {
        newEnd = now;
      }

      if (isBefore(newEnd, tempStartDate)) {
        newEnd = tempStartDate;
      }

      const maxAllowed = moment(tempStartDate).add(15, 'days').toDate();
      if (isAfter(newEnd, maxAllowed)) {
        newEnd = maxAllowed;
      }

      setTempEndDate(newEnd);
      setDateError('');
    }
  };

  const handlePresetRangeSelect = (preset: PresetRange) => {
    const { start, end } = preset.getDateRange();
    setTempStartDate(start);
    setTempEndDate(end);
    setSelectedPreset(preset.value);
    setDateError('');
  };

  const isDateDisabled = (date: MaterialUiPickersDate): boolean => {
    if (!date) return false;

    const minDate = getMinDate();
    const today = new Date();
    return isBefore(date, minDate) || isAfter(date, today);
  };

  const formattedDateRange = `${format(startDate, dateFormat)} to ${format(
    endDate,
    dateFormat
  )}`;

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-popover' : undefined;

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleDateRangeInputClick}
        className="dateRangePickerButton custom-datepicker"
        endIcon={
          <span className="calendarIconWrapper">
            <CalenderIcon />
          </span>
        }
      >
        {formattedDateRange}
      </Button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        className="dateRangePopover"
      >
        <div className="dateRangePickerContainer">
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid container spacing={2} className="datePickerGrid">
              <Grid item xs={6}>
                <DateTimePicker
                  autoOk
                  variant="inline"
                  inputVariant="outlined"
                  label="Start Date Time"
                  format="dd/MM/yyyy, hh:mm a"
                  value={tempStartDate}
                  onChange={handleStartDateChange}
                  disableFuture
                  shouldDisableDate={isDateDisabled}
                  className={`datePicker ${classes.datePicker}`}
                  minDate={startDateMinDate}
                  maxDate={startDateMaxDate}
                />
              </Grid>
              <Grid item xs={6}>
                <DateTimePicker
                  autoOk
                  variant="inline"
                  inputVariant="outlined"
                  label="End Date Time"
                  format="dd/MM/yyyy, hh:mm a"
                  value={tempEndDate}
                  onChange={handleEndDateChange}
                  disableFuture
                  minDate={tempStartDate}
                  maxDate={calculateEndDateMax(tempStartDate)}
                  shouldDisableDate={isDateDisabled}
                  className={`datePicker ${classes.datePicker}`}
                />
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>

          {showPresetRanges && (
            <div className="presetRangeSection">
              <div className="presetRangeHeader">
                <p className="presetRangeTitle">Quick Select (Past Periods)</p>
                <p className="presetRangeDescription">
                  Choose from predefined ranges for faster analysis
                </p>
              </div>

              <div className="presetRangeContainer">
                <div className="presetRangeLabel">Past:</div>
                <div className="presetRangeButtons">
                  {presetRanges.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => handlePresetRangeSelect(preset)}
                      className={`presetRangeButton ${
                        selectedPreset === preset.value ? 'selected' : ''
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {dateError && (
            <div className="dateErrorMessage">
              <Typography color="error" variant="caption">
                {dateError}
              </Typography>
            </div>
          )}

          <div className="dateRangeActions">
            <Button onClick={handleClose} className="cancelButton">
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              color="primary"
              variant="contained"
              className="applyButton url-monitoring"
            >
              Apply
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default DateTimeSelector;
